import { Suspense, lazy, FC } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';
import { env } from 'src/env';
import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import pages from './routes';
import AuthGuard from 'src/Guards/authGuard/AuthGuard';
import Unverified from 'src/pages/Fallbacks/Status/Unverified/Unverified';

import RedirectExternal from 'src/components/RedirectExternal/RedirectExternal';
import ManagementSettingsPage from 'src/pages/Settings/ManagementSettings';
import ApprovePage from 'src/pages/Approve/Approve';
import AttachmentPage from 'src/pages/Attachment/Attachment';

const Loader = (Component: FC) => (props: any) => (
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

// Pages
const Register = Loader(lazy(() => import('src/pages/Register/Register')));
const DueDiligenceFormPage = Loader(lazy(() => import('src/pages/DueDiligenceForm/DueDiligenceForm')));
const Login = Loader(lazy(() => import('src/pages/Login/Login')));
const AcceptInvitePage = Loader(lazy(() => import('src/pages/AcceptInvite/AcceptInvite')));
const DisclosureOverviewPage = Loader(lazy(() => import('src/pages/DisclosureOverview')));
const DisclosureList = Loader(lazy(() => import('src/pages/DisclosureList')));
const CaseOverviewPage = Loader(lazy(() => import('src/pages/CaseOverview')));
const CaseList = Loader(lazy(() => import('src/pages/CaseList')));
const ProfilePage = Loader(lazy(() => import('src/pages/Profile/Profile')));
const ForgotPasswordPage = Loader(lazy(() => import('src/pages/ForgotPassword/ForgotPassword')));
const ApiPlaygroundPage = Loader(lazy(() => import('src/pages/ApiPlayground/ApiPlayground')));
// Status
const Status404 = Loader(lazy(() => import('src/pages/Fallbacks/Status/Status404/Status404')));
const Status500 = Loader(lazy(() => import('src/pages/Fallbacks/Status/Status500/Status500')));
const StatusComingSoon = Loader(lazy(() => import('src/pages/Fallbacks/Status/ComingSoon/ComingSoon')));
const StatusMaintenance = Loader(lazy(() => import('src/pages/Fallbacks/Status/Maintenance/Maintenance')));
const StatusSuccess = Loader(lazy(() => import('src/pages/Fallbacks/Status/Success/Success')));
const StatusFailure = Loader(lazy(() => import('src/pages/Fallbacks/Status/Failure/Failure')));
const StatusCancel = Loader(lazy(() => import('src/pages/Fallbacks/Status/Cancel/Cancel')));

const routes: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    /**
     * All children within this element will NOT have a Sidebar and top Navbar
     * All children within this element does not need to be authenticated to access
     */
    children: [
      //#region Base
      {
        //Navigate to home when base routed to base path
        path: '/',
        element: <Navigate to={pages.home.path} replace />,
      },
      //#endregion Base
      //#region Form Redirect
      {
        path: pages.form.name,
        element: <RedirectExternal to={process.env.REACT_APP_DISCLOSURE_FORM_URL} />,
      },
      //#endregion Form Redirect
      //#region Approval Redirect
      {
        path: pages.approve.name,
        element: <ApprovePage />,
      },
      //#endregion Approval Redirect
      //#region Attachment Redirect
      {
        path: pages.attachment.name,
        element: <AttachmentPage />,
      },
      //#endregion Attachment Redirect
      //#region Auth
      {
        //All authentication routes
        //No navbars are shown as the user is not logged in
        path: pages.auth.root,
        children: [
          {
            path: '',
            element: <Login />,
          },
          {
            path: pages.auth.login.name,
            element: <Login />,
          },
          {
            path: pages.auth.register.name,
            element: <Register />,
          },

          { path: pages.auth.forgotPassword.name, element: <ForgotPasswordPage /> },
          { path: pages.auth.acceptInvite.name, element: <AcceptInvitePage /> },
        ],
      },
      //#endregion Auth
      //#region Status
      {
        //All status routes
        path: pages.status.root,
        children: [
          {
            path: '',
            element: <Navigate to='404' replace />,
          },
          {
            path: pages.status.unverified.name,
            element: <Unverified />,
          },
          {
            path: pages.status.status404.name,
            element: <Status404 />,
          },
          {
            path: pages.status.status500.name,
            element: <Status500 />,
          },
          {
            path: pages.status.statusMaintenance.name,
            element: <StatusMaintenance />,
          },
          {
            path: pages.status.statusComingSoon.name,
            element: <StatusComingSoon />,
          },
          //TODO: Maybe make paths that shows status within the sidebars as well
          {
            path: pages.status.statusSuccess.name,
            element: <StatusSuccess />,
          },
          {
            path: pages.status.statusFailure.name,
            element: <StatusFailure />,
          },
          {
            path: pages.status.statusCancel.name,
            element: <StatusCancel />,
          },
        ],
      },
      //#endregion Status
      //#region NotFound
      {
        path: '*',
        element: <Status404 />,
      },
      //#endregion NotFound
    ],
  },
  {
    path: '',
    element: (
      /**
       * All children with this element will have a Sidebar and top Navbar
       * AuthGuard checks that the user is logged in before granting access to its children pages
       */
      <AuthGuard>
        <SidebarLayout />
      </AuthGuard>
    ),
    children: [
      //#region Base
      {
        path: '',
        element: <Navigate to={pages.home.name} replace />,
      },
      //#endregion Base
      //#region Home
      {
        path: pages.home.name,
        element: <DisclosureList />,
      },
      //#endregion Home
      //#region Disclosures
      {
        path: pages.disclosures.root,
        children: [
          {
            path: 'id/:id',
            element: <DisclosureOverviewPage />,
          },
          {
            path: 'id/:id/due-diligence',
            element: <DueDiligenceFormPage />,
          },
        ],
      },
      // #endregion
      {
        path: pages.settings.name,
        element: <ManagementSettingsPage />,
      },
      {
        path: pages.profile.name,
        element: <ProfilePage />,
      },
      {
        path: pages.cases.root,
        children: [
          { index: true, element: <CaseList /> },
          { path: ':id', element: <CaseOverviewPage /> },
        ],
      },
      ...(env.REACT_APP_DEPLOYMENT_ENV === 'DEV'
        ? [
            {
              path: pages.apiPlayground.name,
              element: <ApiPlaygroundPage />,
            },
          ]
        : []),
    ],
  },
];

export default routes;
